// External libraries
import * as Yup from "yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// Material UI components

import Iconify from "../iconify";
import AddressListPopup from "./addressList";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/router";
import IconCode from "@/src/lib/iconCode";
import { RHFAutocomplete } from "../hook-form";
import { VISITTYPE } from "@/src/lib/util/config";
import {
    manageLoggedOutUserSession,
    updateSession,
} from "@/src/lib/util/updateSession";
import { useRef } from "react";
import useAuth from "@/src/lib/hooks/useAuth";
import NextAvailability from "./nextAvailablity";
import { homeApiService } from "@/src/lib/api-services/homeApiServices";
import PricingSection from "./pricingSection";
import Link from "next/link";
import { useHODMutation } from "@/src/lib/hooks/useHODMutation";
import { useToast } from "@/src/lib/hooks/useToast";
import CustomIconButton from "@/src/lib/ui/iconButton";
import CustomTooltip from "@/src/lib/ui/customTooltip";

const DEFAULT_PINCODE = "110001";
function deepEqual(obj1, obj2) {
    if (obj1 === obj2) return true;

    if (
        typeof obj1 !== "object" ||
        obj1 === null ||
        typeof obj2 !== "object" ||
        obj2 === null
    ) {
        return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (let key of keys1) {
        if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
}

export default function VisitTypeForm({
    testDetails,
    handleTestSelection,
    currentVisitType,
    setCurrentCentre,
    ISLOGGEDIN,
    currentCentre,
    currentTestInfo,
    addressDetails,
    setIsAddressListOpen,
    isAddressListOpen,
    setCurrentCity,
}) {
    const { isLoggedIn } = useAuth();
    const defaultTestId = testDetails?.defaultItemId || null;
    const router = useRouter();
    const dispatch = useDispatch();
    const PACKAGE = 3;
    const RADIOLOGY = 2;

    const userDetails = useSelector((state) => state?.user?.userInfo);

    const currentCentreDetails = userDetails?.centreDetails;
    const configCentre = testDetails?.configCentre || [];
    const prevCentreDetailsRef = useRef({
        cityId: null,
        centreId: null,
        //  stateCode: null,
    });

    const productType = testDetails?.productTypeId;

    const HOOKCITYID = useSelector((state) => state?.hookCity?.cityId);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedCentre, setSelectedCentre] = useState(null);
    //const [selectedTest, setSelectedTest] = useState(null);

    //const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const [centreList, setCentreList] = useState([]);
    const [itemList, setItemList] = useState([]);
    const [pincode, setPincode] = useState(DEFAULT_PINCODE);

    // Form validation schema with Yup
    const centreForm = Yup.object().shape({
        state: Yup.object().required("State is required"),
        city: Yup.object().required("City is required"),
        centre: Yup.object().required("Centre is required"),
        test: Yup.object(),
    });

    const defaultValues = useMemo(
        () => ({
            state: null,
            city: null,
            centre: null,
            test: null,
            address: null,
        }),
        []
    );

    const pincodeChangeMutation = useHODMutation(
        (data) => homeApiService.getHomeRatesBasedOnPincode(data),
        {
            onSuccess: (data) => {
                handleTestsManagement(data);
            },
            onError: (error) => {
                //showToast(error, "error");
            },
        }
    );

    const handleAddressChange = useCallback(async (address) => {
        const { pincode } = address;
        if (pincode) {
            setPincode(pincode);
            const { urlSlug } = testDetails;

            await pincodeChangeMutation.mutate({ urlSlug, pincode });
        }

        isLoggedIn && (await updateAddressInSession(address));
    }, []);

    const updateAddressInSession = useCallback(async (address) => {
        const sessionUpdateType = "ADDRESS"; // or 'CENTRE', 'CART', 'HOOKCITY'
        await updateSession(sessionUpdateType, { address }, dispatch);
    }, []);

    // const getStates = useCallback(() => {
    //     return Object.keys(configCentre).map((stateCode) => ({
    //         stateCode,
    //         state: configCentre[stateCode].state,
    //     }));
    // }, [configCentre]);

    const getCities = useCallback(
        () =>
            Object.values(configCentre).map((item) => ({
                city: item.city,
                cityId: item.cityId,
                stateCode: item.stateCode,
            })),
        [configCentre]
    );

    const getCentres = useCallback(
        (cityId) => {
            try {
                const city = configCentre[cityId];
                if (!city) return [];

                return Object.keys(city?.centres).map((centreId) => ({
                    centreId: Number(centreId),
                    ...city.centres[centreId],
                }));
            } catch (error) {
                console.error("Error getting centres:", error);
            }
        },
        [configCentre]
    );

    const getItems = useCallback(
        (cityId, centreId) => {
            if (productType == RADIOLOGY) {
                const getAllTheCentres = Object.values(configCentre).map(
                    (item) => Object.values(item.centres)
                );

                const flatArrayAllTheCentres = getAllTheCentres.flatMap(
                    (item) => item
                );

                const findCentre = flatArrayAllTheCentres.find(
                    (centre) => centre.centreId === centreId
                );

                return findCentre?.items || [];
            } else {
                const city = configCentre[cityId];
                if (!city) return [];
                const centre = city?.centres[centreId];
                if (!centre) return [];
                return centre.items || [];
            }
        },
        [configCentre]
    );

    const methods = useForm({
        resolver: yupResolver(centreForm),
        defaultValues,
    });

    const { setValue, handleSubmit, watch } = methods;

    const currentTest = watch("test");

    const reorderCities = (configCentre, cityId) => {
        if (!configCentre) {
            return [];
        }

        const priority = { DL: 1, UP: 2, HR: 3 };

        return Object.values(configCentre)
            .map((city) => ({
                ...city,
                priorityOrder:
                    city.cityId === cityId ? 0 : priority[city.stateCode] || 99,
            }))
            .sort((a, b) => a.priorityOrder - b.priorityOrder);
    };

    useEffect(() => {
        let centres = [];
        if (productType == RADIOLOGY) {
            const allCentres = reorderCities(
                configCentre,
                selectedCity?.cityId
            ).map((item) => Object.values(item.centres));
            centres = allCentres.flatMap((item) => item);

            setCentreList(centres);
        } else {
            centres = getCentres(selectedCity?.cityId);

            const sortedData = centres.sort((a, b) => {
                // Prioritize "1#" in the sortkey
                if (
                    a?.sortKey?.startsWith("1#") &&
                    !b?.sortKey?.startsWith("1#")
                ) {
                    return -1; // a comes first
                } else if (
                    !a?.sortKey?.startsWith("1#") &&
                    b?.sortKey?.startsWith("1#")
                ) {
                    return 1; // b comes first
                } else {
                    return 0; // maintain relative order for others
                }
            });

            setCentreList(sortedData);
        }

        const { centreId } = currentCentreDetails || {};
        let selectedCentreFromSession = centres.find((centre) => {
            const isMatchingCentre = centre.centreId === centreId;
            const hasTestsAvailable =
                getItems(selectedCity?.cityId, centre.centreId).length > 0;

            return isMatchingCentre && hasTestsAvailable;
        });

        if (!selectedCentreFromSession) {
            selectedCentreFromSession = centres.find(
                (centre) =>
                    getItems(selectedCity?.cityId, centre.centreId).length > 0
            );
        }

        if (!selectedCentreFromSession) {
            selectedCentreFromSession = centres[0];
        }

        if (!deepEqual(selectedCentre, selectedCentreFromSession)) {
            setValue("centre", selectedCentreFromSession);
            setSelectedCentre(selectedCentreFromSession);
        }
    }, [selectedCity, currentCentreDetails, testDetails]);

    useEffect(() => {
        const cities = getCities();

        const cityId =
            (ISLOGGEDIN && currentCentreDetails?.cityId) || HOOKCITYID;
        setCityList(reorderCities(cities, cityId));

        const selectedCityFromSession = cities.find(
            (city) => city.cityId === cityId
        );

        const newSelectedCity =
            cityId && selectedCityFromSession
                ? selectedCityFromSession
                : cities[0];

        if (!deepEqual(selectedCity, newSelectedCity)) {
            setValue("city", newSelectedCity);
            setSelectedCity(newSelectedCity);
        }
    }, [selectedState, currentCentreDetails, ISLOGGEDIN, HOOKCITYID]);

    useEffect(() => {
        const prevCentreDetails = prevCentreDetailsRef.current;
        const currentCentreDetails = {
            cityId: selectedCity?.cityId,
            centreId: selectedCentre?.centreId,
            // stateCode: selectedState?.stateCode,
        };

        const isData =
            currentCentreDetails.centreId && currentCentreDetails.cityId;

        // shallow comparison as a fallback for identical object structures
        if (
            prevCentreDetails.cityId !== currentCentreDetails.cityId ||
            prevCentreDetails.centreId !== currentCentreDetails.centreId
        ) {
            prevCentreDetailsRef.current = currentCentreDetails;
            const sessionUpdateType = "CENTRE";
            const updateData = { centreDetails: currentCentreDetails };
            //isData && updateSession(sessionUpdateType, updateData, dispatch);

            const sessionHandler = isLoggedIn
                ? updateSession
                : manageLoggedOutUserSession;

            if (isData) {
                // sessionHandler(sessionUpdateType, updateData, dispatch);
            }
        }
    }, [selectedCentre]);

    const handleTestsManagement = (hcRates) => {
        // as it is async function, adding a check to avoid setting the itemList when the visit type is changed
        if (currentVisitType !== VISITTYPE.HOME) {
            return;
        }
        setItemList(hcRates || []);
        const defaultItem = hcRates.find(
            (test) => test.itemId === defaultTestId
        );

        const item = defaultItem ? defaultItem : hcRates[0];
        setValue("test", item);
        handleTestSelection(item);
    };

    const fetchHomeRates = () => {
        const { urlSlug } = testDetails;

        if (urlSlug) {
            pincodeChangeMutation.mutate({
                urlSlug,
                pincode: !ISLOGGEDIN ? pincode : null,
            });
        }
    };

    useEffect(() => {
        if (currentVisitType == VISITTYPE.HOME) {
            fetchHomeRates();
        } else {
            const tests = getItems(
                selectedCity?.cityId,
                selectedCentre?.centreId
            );

            if (tests.length !== 0) {
                setItemList(tests);

                const defaultItem = tests.find(
                    (test) => test.itemId === defaultTestId
                );

                setValue("test", defaultItem ? defaultItem : tests[0]);
                setCurrentCentre(selectedCentre);
                setCurrentCity(selectedCity?.cityId);
            } else {
                setItemList([]);
                setValue("test", null);
            }
        }
    }, [
        selectedCentre,
        currentVisitType,
        ISLOGGEDIN,
        configCentre,
        testDetails,
    ]);

    useEffect(() => {
        if (currentTest) {
            handleTestSelection(currentTest);
        } else {
            handleTestSelection(itemList[0]);
        }
    }, [currentTest, itemList]);

    const handleStateChange = useCallback(
        async (event, value) => {
            try {
                if (value && value !== selectedState) {
                    setSelectedState(value);
                    setValue("state", value);
                }
            } catch (error) {
                console.error("Error updating session:", error);
            }
        },
        [setSelectedState]
    );

    const handleCityChange = useCallback(
        async (event, value) => {
            try {
                if (value) {
                    setSelectedCity(value);
                    setValue("city", value);
                }
            } catch (error) {
                console.error("Error updating session:", error);
            }
        },
        [setSelectedCity]
    );

    const handleCentreChange = useCallback(
        async (event, value) => {
            try {
                if (value) {
                    setSelectedCentre(value);
                    setValue("centre", value);
                }
            } catch (error) {
                console.error("Error updating session:", error);
            }
        },
        [setSelectedCentre]
    );

    const handleTestChange = useCallback(
        (event, value) => {
            if (value) {
                setValue("test", value);
                handleTestSelection(value);
            }
        },
        [handleTestSelection, setValue]
    );

    return (
        <>
            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit()}
                    //	className="p-8 bg-white rounded-lg shadow-md"
                >
                    <div container className="grid grid-cols-1 gap-4">
                        {ISLOGGEDIN && currentVisitType === VISITTYPE.HOME && (
                            <div className="w-full">
                                <h6 className="mb-1.5 block text-sm font-medium text-slate-700">
                                    Address
                                </h6>

                                <div className="flex items-start gap-3 rounded-lg border border-solid border-slate-200 bg-slate-50 p-3">
                                    <Iconify
                                        icon={IconCode.outlinedLocation}
                                        width={20}
                                        className="mt-0.5 shrink-0 text-slate-400"
                                    />
                                    <div className="min-w-0 flex-1">
                                        {!!addressDetails ? (
                                            <>
                                                <p className="text-sm font-semibold leading-5 text-slate-900 line-clamp-2">
                                                    {addressDetails?.address}
                                                </p>
                                                <p className="mt-0.5 text-xs text-slate-500">
                                                    {addressDetails?.pincode}
                                                </p>
                                            </>
                                        ) : (
                                            <p className="text-sm text-slate-500">
                                                No address selected
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className="shrink-0 cursor-pointer rounded-sm text-sm font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                                        onClick={() =>
                                            setIsAddressListOpen(true)
                                        }
                                        aria-label="change address"
                                    >
                                        Change
                                    </button>
                                </div>
                            </div>
                        )}
                        {!ISLOGGEDIN && currentVisitType === VISITTYPE.HOME && (
                            <NextAvailability
                                handlePincodeChange={handleAddressChange}
                                pincode={pincode}
                            />
                        )}
                        {/* State Autocomplete */}

                        {/* City Autocomplete */}

                        {currentVisitType === VISITTYPE.CENTRE && (
                            <div
                                className={`w-full ${
                                    productType == RADIOLOGY
                                        ? "hidden"
                                        : "block"
                                }`}
                            >
                                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                                    City
                                </label>
                                <RHFAutocomplete
                                    name="city"
                                    aria-label="City"
                                    //label="City"
                                    options={cityList || []}
                                    size="small"
                                    disableClearable={true}
                                    getOptionLabel={(option) => option.city}
                                    isOptionEqualToValue={(option, value) =>
                                        option.value === value.cityId
                                    }
                                    onChange={(event, newValue) => {
                                        handleCityChange(event, newValue);
                                    }}
                                    className="w-full"
                                />
                            </div>
                        )}

                        {/* Centre Autocomplete */}
                        {currentVisitType === VISITTYPE.CENTRE && (
                            <div className="w-full">
                                <label
                                    htmlFor="centre"
                                    className="mb-1.5 block text-sm font-medium text-slate-700"
                                >
                                    Centres
                                </label>

                                <div className="flex flex-row items-center gap-2 w-full">
                                    <RHFAutocomplete
                                        id="centre"
                                        name="centre"
                                        aria-label="centre"
                                        //label="Centre"
                                        options={centreList || []}
                                        size="small"
                                        disableClearable={true}
                                        getOptionLabel={(option) =>
                                            option.centre
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.value === value.centreId
                                        }
                                        onChange={(event, newValue) => {
                                            handleCentreChange(event, newValue);
                                        }}
                                        className="w-full"
                                    />

                                    <CustomTooltip
                                        title="View Location"
                                        arrow={true}
                                    >
                                        <Link
                                            href={`/locate-us/${selectedCentre?.urlSlug}`}
                                            target="_blank"
                                            className="cursor-pointer shrink-0"
                                            aria-label="View centre location"
                                        >
                                            <CustomIconButton
                                                shape="rounded"
                                                className="h-10 w-10 cursor-pointer rounded-lg! border border-solid border-slate-200 hover:border-primary/40 hover:bg-primary/5!"
                                            >
                                                <Iconify
                                                    width={20}
                                                    icon={"ri:direction-fill"}
                                                    className="text-primary"
                                                />
                                            </CustomIconButton>
                                        </Link>
                                    </CustomTooltip>
                                </div>
                            </div>
                        )}

                        {/* Test Autocomplete */}
                        {productType !== PACKAGE && (
                            <div className="w-full">
                                <label
                                    htmlFor="test"
                                    className="mb-1.5 block text-sm font-medium text-slate-700"
                                >
                                    Tests
                                </label>

                                <RHFAutocomplete
                                    name="test"
                                    aria-label="test"
                                    id="test" // ✅ connects with the <label>
                                    options={itemList || []}
                                    size="small"
                                    disableClearable={true}
                                    getOptionLabel={(option) => option.itemName}
                                    isOptionEqualToValue={(option, value) =>
                                        option.value === value.itemId
                                    }
                                    onChange={(event, newValue) => {
                                        handleTestChange(event, newValue);
                                        //setValue("test", newValue);
                                    }}
                                    className="w-full"
                                />

                                {itemList.length > 1 && (
                                    <p className="mt-1.5 text-xs leading-5 text-primary">
                                        {`*There are ${itemList.length} test options, please select the correct required test`}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </form>
            </FormProvider>
            <PricingSection
                ISLOGGEDIN={ISLOGGEDIN}
                visitType={currentVisitType}
                currentTest={currentTestInfo}
            />
            <AddressListPopup
                open={isAddressListOpen}
                onClose={() => setIsAddressListOpen(false)}
                handleAddressChange={handleAddressChange}
                selectedAddress={addressDetails?.addressGuid}
            />
        </>
    );
}
